// NPM
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { omitBy, isNull, isEmpty } from "lodash";
import moment from "moment";

// UTILITY
import errorHandler from "Utils/error-handler";

// VALIDATOR
import { SpecialConditionCareSchema } from "./special-condition-care-validation";

// STYLE
import "./special-condition-care-form.scss";

// ANT COMPONENTS
import { Checkbox, Col, Row } from "antd";
import { 
    AlertBox, 
    Button, 
    DateRange, 
    TextInput 
} from "@life-line-lab/components-react";

interface SpecialConditionCareFormComponentProps {
    info?: any;
    onSubmit?: any;
    onUpdate?: any;
}

const SpecialConditionCareFormComponent: React.FC<SpecialConditionCareFormComponentProps> = ({
    info,
    onSubmit,
    onUpdate,
}) => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const { type } = useParams<{ type: string }>();

    const initialInput = {
        name: "",
        isFixedAmount: false,
        isPercentage: false,
        fixedAmount: "",
        percentage: "",
        capAmount: "",
        validFrom: {
            date: null,
            dateString: "",
        },
        validTo: {
            date: moment('31/12/9999', 'DD/MM/YYYY'),
            dateString: moment('31/12/9999', 'DD/MM/YYYY').toISOString(),
        },
        maxWeight: "",
        length: "",
        width: "",
        height: "",
    };
    const initialError = {};
    const [input, setInput] = useState(initialInput);
    const [error, setError] = useState<any>(initialError);
    
    useEffect(() => {
        document.body.classList.add("stack-screen--open");
        return () => document.body.classList.remove("stack-screen--open");
    }, []);
    
    useEffect(() => {
        if (info) {
            setInput({
                ...input,
                ...info,
                validFrom: { date: info?.validFrom, dateString: moment(info?.validFrom).toISOString() },
                validTo: { date: info?.validTo, dateString: moment(info?.validTo).toISOString() },
            });
        }
    }, [info]);

    const handleCheckboxChange = (checked: boolean, name: string) => {
        setError(initialError);
        setInput({ ...input, [name]: checked });
    };

    const handleDateChange = (startDate: any, endDate: any) => {
        if (startDate.date) {
            setInput({ ...input, validFrom: startDate });
            setError({ initialError });
        } else if (endDate.date) {
            setInput({ ...input, validTo: endDate });
            setError({ initialError });
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setError(initialError);
        setInput({ ...input, [e.target.name]: e.target.value });
    };

    const handleSubmit = () => {
        try {
            const valInput = {
                ...input,
                settingID: info?._id,
                surcharge: omitBy({ 
                    // selected but empty attr is unvalidated if pair is valid
                    fixedAmount: input.isFixedAmount ? input.fixedAmount : "",
                    percentage: input.isPercentage ? input.percentage : "",
                    capAmount: input.capAmount
                },isEmpty),
                maxDimension: {
                    length: input.length,
                    width: input.width,
                    height: input.height,
                },
                validFrom: moment(input?.validFrom?.date).toISOString(),
                validTo: moment(input?.validTo?.date).toISOString(),
            };
            const value = SpecialConditionCareSchema.validateSync(omitBy(valInput, isNull), {
                strict: false,
                abortEarly: true,
                stripUnknown: true,
            });
            info ? onUpdate(value) : onSubmit(value);
        } catch (err) {
            console.log(err);
            const { path, value }: any = errorHandler.validation(err);
            setError({ ...error, [path]: t(`${value}`, { field: t(`label.${path}`) }) });
        }
    };

    return (
        <div className="special-condition-care-form">
            <form>
                <div className="main-header">
                    <span className="center header-title">
                        {type == "0"
                            ? t("label.specialCare").toUpperCase()
                            : t("label.specialConditions").toUpperCase()}
                    </span>
                </div>
                <Row gutter={[20, 0]} className="mt-40">
                    <Col xs={24} sm={24} md={12} className="mb-30 alert-box">
                        <TextInput
                            isRequired={true}
                            label={type == "0" ? t("label.specialCare") : t("label.specialConditions")}
                            placeholder={t("txt.enterHere")}
                            value={input.name}
                            name="name"
                            onChange={handleChange}
                        />
                        <AlertBox text={error.name} />
                    </Col>
                    <Col xs={24} sm={24} md={12} className="mb-30 alert-box">
                        <TextInput
                            isRequired={true}
                            label={t("label.maxWeight")}
                            placeholder={t("txt.maxWeight")}
                            value={input.maxWeight}
                            name="maxWeight"
                            onChange={handleChange}
                        />
                        <AlertBox text={error.maxWeight} />
                    </Col>
                    <Col xs={24} sm={24} md={12} className="mb-30">
                        <label className="inp-label inp-label--required">{t("label.surcharge")}</label>
                        <Row gutter={[20, 0]} align="middle">
                            <Col xs={24} sm={24} md={10} className="mb-10 pr-0">
                                <Checkbox
                                    checked={input.isFixedAmount}
                                    onChange={(e) => handleCheckboxChange(e.target.checked, "isFixedAmount")}
                                >
                                    {t(`label.fixedAmount`)}
                                </Checkbox>
                            </Col>
                            <Col xs={24} sm={24} md={14} className="mb-10">
                                <TextInput
                                    isRequired={true}
                                    isReadonly={!input.isFixedAmount}
                                    prefix={t("txt.rm")}
                                    value={input.fixedAmount}
                                    name="fixedAmount"
                                    onChange={handleChange}
                                />
                            </Col>
                        </Row>
                        <Row gutter={[18, 0]} align="middle">
                            <Col xs={24} sm={24} md={10} className="mb-10 pr-0">
                                <Checkbox
                                    checked={input.isPercentage}
                                    onChange={(e) => handleCheckboxChange(e.target.checked, "isPercentage")}
                                >
                                    {t("label.percentage")}
                                </Checkbox>
                            </Col>
                            <Col xs={24} sm={24} md={14} className="mb-0">
                                <TextInput
                                    isRequired={true}
                                    isReadonly={!input.isPercentage}
                                    suffix={t("txt.percentage")}
                                    value={input.percentage}
                                    name="percentage"
                                    onChange={handleChange}
                                />
                            </Col>
                        </Row>
                        {input.isPercentage 
                            ? <Row gutter={[20, 0]} align="middle">
                                <Col xs={24} sm={24} md={10} className="mt-10 mb-0 pr-0">
                                    <label className="ml-24">{t(`label.capAmount`)}</label>
                                </Col>
                                <Col xs={24} sm={24} md={14} className="mt-10 mb-0">
                                    <TextInput
                                        isRequired={true}
                                        prefix={t("txt.rm")}
                                        value={input.capAmount}
                                        name="capAmount"
                                        onChange={handleChange}
                                    />
                                </Col>
                            </Row>
                            : <></>}
                        <Row>
                            <Col span={24} className="mb-0 alert-box">
                                <AlertBox
                                    text={error["surcharge.fixedAmount"] || error["surcharge.percentage"]
                                        ? t("error.surcharge") 
                                        : error["surcharge.capAmount"]
                                        ? t(`error.pattern`, { field: t(`label.capAmount`) }) 
                                        : null}
                                />
                            </Col>
                        </Row>
                        <Row gutter={[15, 15]} className="mt-30">
                            <Col xs={24} sm={24} md={24} className="mb-0 alert-box">
                                <DateRange
                                    isRequired={true}
                                    isError={!!error.identificationIssueDate}
                                    startLabel={t("label.validFrom")}
                                    endLabel={t("label.validTo")}
                                    value={{
                                        startDate: input.validFrom,
                                        endDate: input.validTo,
                                    }}
                                    onChange={handleDateChange}
                                />
                                <AlertBox text={error.validFrom || error.validTo} />
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={24} sm={24} md={12} className="alert-special">
                        <TextInput
                            className="mb-10"
                            isRequired={true}
                            label={t("label.maxDimensions")}
                            placeholder={t("txt.length")}
                            value={input.length}
                            name="length"
                            onChange={handleChange}
                        />
                        <AlertBox text={error["maxDimension.length"]} />
                        <TextInput
                            className="mb-10"
                            isRequired={true}
                            placeholder={t("txt.width")}
                            value={input.width}
                            name="width"
                            onChange={handleChange}
                        />
                        <AlertBox text={error["maxDimension.width"]} />
                        <TextInput
                            isRequired={true}
                            placeholder={t("txt.height")}
                            value={input.height}
                            name="height"
                            onChange={handleChange}
                        />
                        <AlertBox text={error["maxDimension.height"]} />
                    </Col>
                </Row>
                <div className="mt-19 mb-50 center">
                    <Button
                        type="primary"
                        label={id 
                            ? t("action.update") 
                            : t("action.create")
                        }
                        size="large"
                        onClick={() => {
                            handleSubmit();
                        }}
                    />
                </div>
            </form>
        </div>
    );
};

export default SpecialConditionCareFormComponent;
